import { FC, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import clsx from 'clsx';
import { ROUTES } from '../../../constants/data';
import { useSpotMomentum } from '../../../hooks/spot/useSpotMomentum';

// Seed the Map (Doge Spotters) momentum chip - plan section 4: visitors see
// motion ("N added this week - being verified") while the approve-first rule
// keeps unvetted pins hidden. Fallback ladder: week count, then live community
// pins, then a plain CTA (which also covers a failed/undeployed backend query).
// Renders nothing only while the first load is in flight. Clicking it is the
// funnel entry: straight to the Spot a business form.
type Props = {
  // The map-cap note ("1,000+ in view") also lives bottom-left; when it shows,
  // the chip lifts a row so the two never overlap.
  lifted?: boolean;
  // Full-screen mobile map: the centered map/list toggle pill owns the bottom
  // row (fixed at 16px + safe-area), so the chip climbs above it - high enough
  // to clear the cap note's row too.
  sheet?: boolean;
};

const SpotMomentumChip: FC<Props> = ({ lifted = false, sheet = false }) => {
  const navigate = useNavigate();
  const { weekCount, liveCount, momentumLoading } = useSpotMomentum();

  if (momentumLoading) {
    return null;
  }

  return (
    <button
      type="button"
      onClick={() => navigate(ROUTES.SPOT_BUSINESS_ROUTE)}
      className={clsx(
        sheet
          ? 'bottom-[calc(72px+env(safe-area-inset-bottom))]'
          : lifted
          ? 'bottom-16'
          : 'bottom-6',
        'absolute left-3 z-[3] flex max-w-[78%] items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-2 text-start text-sm font-medium text-gray-700 shadow-md hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-500'
      )}>
      <span aria-hidden="true">📍</span>
      <span className="truncate">
        {weekCount > 0 ? (
          <FormattedMessage
            id="map.spot_momentum.week"
            defaultMessage="{count, plural, one {# business} other {# businesses}} added this week - being verified"
            values={{ count: weekCount }}
          />
        ) : liveCount > 0 ? (
          <FormattedMessage
            id="map.spot_momentum.live"
            defaultMessage="{count, plural, one {# community spot} other {# community spots}} on the map"
            values={{ count: liveCount }}
          />
        ) : (
          <FormattedMessage
            id="map.spot_momentum.cta_empty"
            defaultMessage="Spot a DOGE business - earn 75 points"
          />
        )}
      </span>
    </button>
  );
};

// Memoized (map plan A4 convention) - overlay content shouldn't re-render on
// every MapboxLe render during pans.
export default memo(SpotMomentumChip);
